/**
 * @module Services/Bff/Presenters/AuthDetail
 * @desc Auth Detail Presenter
 *
 */

import config from 'config';
import { SHIPPING_MODEL, type User } from 'core/domains';
import { formatShortDate, capitalizeFirstLetter } from 'core/utilities';

import type { AuthDetailResponse } from '../../@types';

type BusinessModel = AuthDetailResponse['data']['businessModel'];

function getWithdrawalCenter(businessModel: BusinessModel) {
  const withdrawal = businessModel?.availables?.filter(
    (model) =>
      model.code === config.pickupStore || model.code === SHIPPING_MODEL.KEY_ACCOUNT_PICKUP_STORE,
  );

  const deliveries =
    withdrawal[0]?.deliveries?.filter((deliverie) => deliverie.isWithdrawalCenter) ||
    ({} as BusinessModel['availables'][number]['deliveries']);

  const availables = businessModel?.availables?.map((available) => {
    if (
      available.code === config.pickupStore ||
      available.code === SHIPPING_MODEL.KEY_ACCOUNT_PICKUP_STORE
    ) {
      return {
        code: available.code,
        name: withdrawal[0].name,
        deliveries,
      };
    }

    return available;
  });

  return {
    ...businessModel,
    availables,
  };
}

function getBusinessModelDeliveries(businessModel: BusinessModel) {
  const availables = businessModel?.availables?.find(
    (model) =>
      model.code === config.pickupStore || model.code === SHIPPING_MODEL.KEY_ACCOUNT_PICKUP_STORE,
  )?.deliveries;

  return availables || [];
}

function hasWithdrawalCenter(businessModel: BusinessModel) {
  const hasBusinessModelDefault = Object.keys(getBusinessModelDeliveries(businessModel)).length;

  return Boolean(hasBusinessModelDefault);
}

export const phoneNumberAutoFormat = (phoneNumber: string): string => {
  const number = phoneNumber.trim().replace(/[^0-9]/g, '');

  return number.replace(/(\d{2})(\d{1})/, '($1)$2').replace(/(\d{1})(\d{4})(\d{3})/, ' $1 $2-$3');
};

function parsingSupervisor(comercialStructure: AuthDetailResponse['data']['comercialStructure']) {
  const { contacts } = comercialStructure.supervisor;
  const normalize = contacts.map((contact) => {
    if (contact.type === 'email') {
      return {
        ...contact,
        value: contact.value.toLocaleLowerCase(),
      };
    }

    if (contact.type === 'mobilePhone') {
      return {
        ...contact,
        value: phoneNumberAutoFormat(contact.value),
      };
    }
    return contact;
  });

  return {
    ...comercialStructure.supervisor,
    name: capitalizeFirstLetter(comercialStructure?.supervisor?.name || ''),
    contacts: normalize,
  };
}

/**
 * User Credit Presenter
 *
 */
export function authDetailPresenter(payload: AuthDetailResponse): User['data'] {
  const {
    data: { businessModel, comercialStructure, cycle, reseller, isExtendedCapitation = false },
  } = payload;

  const filteredCycle = cycle.find(
    (cycleIndex) => cycleIndex.cycleNumber === reseller.currentCycle,
  );

  return {
    brand: reseller.origin,
    cycle: {
      currentCycle: reseller.currentCycle,
      daysLeft: filteredCycle?.remainingDays ?? 0,
      daysTotal: filteredCycle?.cycleLength ?? 0,
      finalDate: formatShortDate(filteredCycle?.finalDate ?? ''),
      initialDate: formatShortDate(filteredCycle?.initialDate ?? ''),
      nextCycle: reseller.nextCycle,
    },
    fullAddress: reseller.fullAddress,
    isBlocked: reseller.resellerBlocked,
    isCeased: reseller.resellerCeased,
    isMultiBrand: reseller.isMultiBrand,
    name: reseller.resellerName,
    region: comercialStructure.name,
    resellerId: reseller.resellerId,
    shippingModel: getWithdrawalCenter(businessModel),
    supervisor: parsingSupervisor(comercialStructure) || [],
    isWithdrawalCenter: hasWithdrawalCenter(businessModel),
    isExtendedCapitation,
    avaliableCycles: {} as User['data']['avaliableCycles'],
    isResaleIntention: reseller.isResaleIntention,
    isAutoStartEnabled: reseller.isAutoStartEnabled,
  };
}
